import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { Badge, Col, Row } from "react-bootstrap";
import moment from "moment";
import {
  AddCommasToAmount,
  getSafeDocumentUrl,
  openSafeDocumentUrl,
} from "../../helper/Utils";
import "./sanctionOrderModal.css";

const formatDate = (value) => {
  if (!value) return "—";
  const m = moment(value, ["YYYY-MM-DD", "DD/MM/YYYY", moment.ISO_8601], true);
  return m.isValid() ? m.format("DD-MM-YYYY") : value;
};

const formatAmt = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return "—";
  if (num === 0) return "₹ 0";
  return `₹ ${AddCommasToAmount(value)}`;
};

const display = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return value;
};

const InfoItem = ({ label, value }) => (
  <div className="so-info-item">
    <div className="so-info-label">{label}</div>
    <div className="so-info-value">{value}</div>
  </div>
);

const DocChip = ({ url, label }) => {
  const safeUrl = getSafeDocumentUrl(url);
  if (!safeUrl) return null;
  return (
    <span className="so-doc-chip">
      <i className="icon-picture_as_pdf text-danger-100" />
      <a
        href={safeUrl}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => {
          e.preventDefault();
          openSafeDocumentUrl(url);
        }}
      >
        {label}
      </a>
    </span>
  );
};

const DeductionRow = ({ items }) => (
  <Row className="g-2 so-deduction-row">
    {items.map((item) => (
      <Col xs={6} md={3} key={item.label}>
        <InfoItem label={item.label} value={item.value} />
      </Col>
    ))}
  </Row>
);

const groupActivitiesByName = (subheads = []) => {
  const grouped = {};
  subheads.forEach((subhead) => {
    (subhead?.activities || []).forEach((activityItem) => {
      const groupKey = `${activityItem?.activity_id || ""}_${activityItem?.activity || ""}`;
      if (!grouped[groupKey]) {
        grouped[groupKey] = {
          activity_id: activityItem?.activity_id,
          activity: activityItem?.activity || "—",
          subheadName: subhead?.name || subhead?.code || "",
          subactivities: [],
        };
      }
      const alreadyExists = grouped[groupKey].subactivities.some(
        (item) =>
          String(item?.subactivity_id) === String(activityItem?.subactivity_id)
      );
      if (!alreadyExists) {
        grouped[groupKey].subactivities.push(activityItem);
      }
    });
  });
  return Object.values(grouped);
};

/**
 * Invoice approval confirm preview.
 * Same visual language as Sanction Order Preview.
 * formData = one invoice (each ProceedForApproval instance has its own modal,
 * so multiple invoices on the page stay independent / unbroken).
 */
const ApprovalFundPreview = ({
  showPreview,
  setShowPreview,
  formData,
  remarks,
  l2remarks,
  l3remarks,
  actionStatus,
  handleSubmit,
  invoiceIndex,
}) => {
  const handleClose = () => setShowPreview(false);
  const isReject = Number(actionStatus) === 2;
  const schemes = formData?.scheme_list || [];
  const companyName =
    formData?.beneficiary_other_details?.beneficiary_company_name ||
    formData?.company_name ||
    "—";
  const account = formData?.beneficiary_other_details?.account_details || {};
  const paymentLabel =
    Number(formData?.payment_type) === 2 || Number(formData?.payment) === 2
      ? "Part Payment"
      : Number(formData?.payment_type) === 1 || Number(formData?.payment) === 1
        ? "Full Payment"
        : "—";

  const invoiceLabel =
    invoiceIndex != null ? `Invoice ${invoiceIndex + 1}` : "Invoice";

  return (
    <Modal
      size="xl"
      show={showPreview}
      onHide={handleClose}
      centered
      scrollable
      dialogClassName="so-preview-modal"
    >
      <Modal.Header closeButton className="so-preview-header">
        <Modal.Title>
          Invoice Approval Preview
          {isReject ? " — Reject" : " — Approve"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="so-preview-body">
        <section className="so-block">
          <div className="so-block-title-row">
            <h6 className="so-block-title">Sanction details</h6>
            <div className="so-block-badges">
              <span className="so-soft-badge">{invoiceLabel}</span>
              <Badge bg={isReject ? "danger" : "success"} className="so-payment-badge">
                {isReject ? "Reject" : "Approve"}
              </Badge>
              <DocChip url={formData?.sanction_order} label="Sanction Order" />
            </div>
          </div>
          <Row className="g-2">
            <Col md={4}>
              <InfoItem
                label="Sanction Order No."
                value={display(formData?.sanction_order_no)}
              />
            </Col>
            <Col md={4}>
              <InfoItem
                label="Sanction Order Date"
                value={formatDate(formData?.sanction_order_date)}
              />
            </Col>
            <Col md={4}>
              <InfoItem
                label="Sanction Order Amount"
                value={formatAmt(
                  formData?.sanction_order_value || formData?.sanction_amount
                )}
              />
            </Col>
          </Row>
        </section>

        <section className="so-block">
          <div className="so-block-title-row">
            <h6 className="so-block-title">Voucher</h6>
            <DocChip url={formData?.voucher} label="Voucher doc" />
          </div>
          <Row className="g-2">
            <Col md={4}>
              <InfoItem
                label="Voucher No."
                value={display(formData?.voucher_no)}
              />
            </Col>
            <Col md={4}>
              <InfoItem
                label="Voucher Date"
                value={formatDate(formData?.voucher_date)}
              />
            </Col>
            <Col md={4}>
              <InfoItem
                label="Voucher Amount"
                value={formatAmt(formData?.voucher_amount)}
              />
            </Col>
          </Row>
        </section>

        <section className="so-block">
          <div className="so-block-title-row">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <h6 className="so-block-title mb-0">{invoiceLabel}</h6>
              <Badge
                bg={
                  Number(formData?.payment_type) === 2 ||
                  Number(formData?.payment) === 2
                    ? "warning"
                    : "primary"
                }
                className="so-payment-badge"
              >
                {paymentLabel}
              </Badge>
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <DocChip url={formData?.invoice} label="Invoice Order" />
              <DocChip url={formData?.invoice_ref} label="Invoice Ref" />
              <strong className="so-invoice-amount">
                {formatAmt(formData?.sanction_amount || formData?.invoice_value)}
              </strong>
            </div>
          </div>

          <Row className="g-2">
            <Col md={4}>
              <InfoItem label="Beneficiary" value={companyName} />
            </Col>
            <Col md={4}>
              <InfoItem
                label="Invoice No."
                value={display(formData?.invoice_no)}
              />
            </Col>
            <Col md={4}>
              <InfoItem
                label="Invoice Date"
                value={formatDate(formData?.invoice_date)}
              />
            </Col>
            <Col md={4}>
              <InfoItem
                label="Invoice Amount"
                value={formatAmt(formData?.invoice_value)}
              />
            </Col>
            <Col md={4}>
              <InfoItem
                label="Taxable Amount"
                value={formatAmt(formData?.taxable_amount)}
              />
            </Col>
            <Col md={4}>
              <InfoItem
                label="Sanctioned Amount"
                value={formatAmt(formData?.sanction_amount)}
              />
            </Col>
            <Col md={4}>
              <InfoItem
                label="Total Deduction"
                value={formatAmt(formData?.total_deduction)}
              />
            </Col>
            <Col md={4}>
              <InfoItem
                label="Withheld"
                value={formatAmt(formData?.withheld_amount)}
              />
            </Col>
            <Col md={4}>
              <InfoItem
                label="Payment Amount"
                value={
                  <span className="text-success">
                    {formatAmt(formData?.payable_amount)}
                  </span>
                }
              />
            </Col>
          </Row>

          {(account?.account_no || account?.beneficiary_name) && (
            <>
              <h6 className="so-subtitle">Beneficiary account</h6>
              <Row className="g-2">
                <Col md={4}>
                  <InfoItem
                    label="Account Holder"
                    value={display(account?.beneficiary_name)}
                  />
                </Col>
                <Col md={4}>
                  <InfoItem
                    label="Account No."
                    value={display(account?.account_no)}
                  />
                </Col>
                <Col md={4}>
                  <InfoItem label="Bank" value={display(account?.bank_name)} />
                </Col>
                <Col md={4}>
                  <InfoItem
                    label="Project"
                    value={display(account?.project_name)}
                  />
                </Col>
                <Col md={4}>
                  <InfoItem label="IFSC" value={display(account?.ifsc_code)} />
                </Col>
              </Row>
            </>
          )}

          <h6 className="so-subtitle">Deductions</h6>
          <DeductionRow
            items={[
              { label: "GST", value: formatAmt(formData?.gst) },
              { label: "GIS", value: formatAmt(formData?.gis) },
              { label: "NPS", value: formatAmt(formData?.nps) },
              {
                label: "Total Deduction",
                value: formatAmt(formData?.total_deduction),
              },
            ]}
          />

          <div className="so-subtitle-row">
            <h6 className="so-subtitle mb-0">Scheme details</h6>
          </div>
          <div className="so-scheme-stack">
            {schemes.length === 0 ? (
              <div className="so-scheme-empty text-muted">No scheme mapped</div>
            ) : (
              schemes.map((scheme, sIndex) => {
                const subheads = scheme?.subheads || [];
                const activityGroups = groupActivitiesByName(subheads);
                return (
                  <div
                    className="so-scheme-card"
                    key={scheme?.invoice_scheme_id || scheme?.id || sIndex}
                  >
                    <div className="so-scheme-section">
                      <div className="so-scheme-section-label">Scheme</div>
                      <Row className="g-2">
                        <Col xs={12}>
                          <InfoItem
                            label="Name"
                            value={display(
                              scheme?.name ||
                                [scheme?.code, scheme?.department]
                                  .filter(Boolean)
                                  .join(" — ")
                            )}
                          />
                        </Col>
                        <Col xs={6} md={4}>
                          <InfoItem
                            label="Code"
                            value={display(scheme?.code)}
                          />
                        </Col>
                        <Col xs={6} md={4}>
                          <InfoItem
                            label="Type"
                            value={display(scheme?.type)}
                          />
                        </Col>
                        <Col xs={6} md={4}>
                          <InfoItem
                            label="FY"
                            value={display(scheme?.financial_year)}
                          />
                        </Col>
                        <Col xs={6} md={4}>
                          <InfoItem
                            label="Amount"
                            value={formatAmt(scheme?.invoice_scheme_amount)}
                          />
                        </Col>
                        <Col xs={6} md={4}>
                          <InfoItem
                            label="Scheme Budget"
                            value={formatAmt(scheme?.total_budget)}
                          />
                        </Col>
                      </Row>
                    </div>

                    {subheads.length > 0 && (
                      <div className="so-scheme-section">
                        <div className="so-scheme-section-label">Subhead</div>
                        {subheads.map((subhead, shIndex) => (
                          <Row
                            className="g-2 mb-2"
                            key={subhead?.id || subhead?.code || shIndex}
                          >
                            <Col xs={12} md={8}>
                              <InfoItem
                                label="Name"
                                value={display(
                                  subhead?.name || subhead?.code
                                )}
                              />
                            </Col>
                            <Col xs={6} md={4}>
                              <InfoItem
                                label="Amount"
                                value={formatAmt(subhead?.subhead_amount)}
                              />
                            </Col>
                          </Row>
                        ))}
                      </div>
                    )}

                    {activityGroups.length > 0 && (
                      <div className="so-scheme-section so-scheme-section-last">
                        <div className="so-scheme-section-label">
                          Activity / Subactivity
                          {activityGroups.length > 1
                            ? ` (${activityGroups.length})`
                            : ""}
                        </div>
                        <div className="so-activity-list">
                          {activityGroups.map((group, gIndex) => (
                            <div
                              className="so-activity-item"
                              key={`${group.activity_id}-${gIndex}`}
                            >
                              {activityGroups.length > 1 && (
                                <div className="so-activity-item-head">
                                  #{gIndex + 1}
                                </div>
                              )}
                              <Row className="g-2">
                                <Col xs={12}>
                                  <InfoItem
                                    label="Activity"
                                    value={display(group.activity)}
                                  />
                                </Col>
                                {group.subactivities.map((act, aIndex) => (
                                  <Col xs={12} key={aIndex}>
                                    <Row className="g-2">
                                      <Col xs={12}>
                                        <InfoItem
                                          label="Subactivity"
                                          value={display(act?.subactivity)}
                                        />
                                      </Col>
                                      <Col xs={6} md={4}>
                                        <InfoItem
                                          label="Amount"
                                          value={formatAmt(act?.amount)}
                                        />
                                      </Col>
                                      <Col xs={6} md={4}>
                                        <InfoItem
                                          label="Deduction"
                                          value={formatAmt(
                                            act?.activity_deduction
                                          )}
                                        />
                                      </Col>
                                    </Row>
                                  </Col>
                                ))}
                              </Row>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </section>

        <section className="so-block">
          <h6 className="so-block-title">Remarks</h6>
          <Row className="g-2 mt-1">
            <Col md={4}>
              <InfoItem label="L1 Remarks" value={display(remarks)} />
            </Col>
            <Col md={4}>
              <InfoItem label="L2 Remarks" value={display(l2remarks)} />
            </Col>
            <Col md={4}>
              <InfoItem label="L3 Remarks" value={display(l3remarks)} />
            </Col>
          </Row>
        </section>
      </Modal.Body>

      <Modal.Footer className="so-preview-footer">
        <Button variant="outline-secondary" size="sm" onClick={handleClose}>
          Change
        </Button>
        <Button
          className={isReject ? "" : "so-preview-submit"}
          variant={isReject ? "danger" : "primary"}
          size="sm"
          onClick={handleSubmit}
        >
          {isReject ? "Confirm Reject" : "Confirm Approve"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ApprovalFundPreview;
